import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import ComponentScatter from '../../../../charts/echarts/ComponentScatter'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import { interceptFormatters } from '../ComponentBar/formatInterceptor'

const useFormatters = (valueFormat, xFormat, yFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => formatter(d, valueFormat), [formatter, valueFormat])
  const xFormatter = useCallback(d => formatter(d, xFormat || valueFormat), [formatter, xFormat, valueFormat])
  const yFormatter = useCallback(d => formatter(d, yFormat || valueFormat), [formatter, yFormat, valueFormat])

  return {
    valueFormatter,
    xFormatter,
    yFormatter,
    formatter
  }
}

function DDPScatterPlot ({
  pipeline,
  variables,

  // Data structure hints
  variant = 'simple', // 'simple', 'multi-series'

  // Context-aware accessors (work differently based on variant)
  xAccessor, // Simple: item.x accessor | Multi-series: seriesItem.x accessor
  yAccessor, // Simple: item.y accessor | Multi-series: seriesItem.y accessor
  sizeAccessor, // For bubble charts: item.size accessor
  labelAccessor, // Accessor to label/name for each point

  // Series accessors - consistent with other DDP visualizations
  seriesAccessor, // Simple: accessor to data array | Multi-series: accessor to series array
  seriesNameAccessor, // Accessor to name within series (e.g., "name", "label")
  seriesDataAccessor, // Accessor to data within series (e.g., "data", "values")

  // Optional data structure hints
  dataFormat, // 'array' | 'xy' | 'object' - helps interpret data structure

  // Symbol size configuration
  symbolSize = 8, // Static number or 'linear' | 'log' | 'exp' | 'outlier'
  sizeMin = 5, // Minimum symbol size for scaling
  sizeMax = 50, // Maximum symbol size for scaling

  // Color palette
  palette, // Array of colors to apply to points

  // Dataset pattern support
  datasetSource, // Accessor to dataset source
  datasetDimensions, // Array of dimension names
  encode, // Static encode mapping

  // Formatting
  valueFormat = 'number',
  xFormat, // Optional specific format for x values
  yFormat, // Optional specific format for y values

  // All other props pass through to ComponentScatter
  ...restProps
}) {
  const { valueFormatter, xFormatter } = useFormatters(valueFormat, xFormat, yFormat)
  const { setSeriesLoading } = useChartContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  // Process data based on variant and configuration
  const processedData = useMemo(() => {
    if (!data) return null

    // Handle dataset pattern
    if (datasetSource && datasetDimensions) {
      const source = get(data, datasetSource) || data
      return {
        dataset: {
          source,
          dimensions: datasetDimensions
        },
        encode: encode || {}
      }
    }

    // Use accessor-based approach if provided
    if (variant === 'simple' && (xAccessor || yAccessor || seriesAccessor)) {
      const rawData = seriesAccessor ? get(data, seriesAccessor) : data
      const result = processSimpleData(rawData, {
        xAccessor, yAccessor, sizeAccessor, labelAccessor, dataFormat, palette
      })
      return result
    }

    if (variant === 'multi-series' && seriesAccessor) {
      const seriesData = get(data, seriesAccessor) || data
      return processMultiSeriesData(seriesData, {
        seriesNameAccessor,
        seriesDataAccessor,
        xAccessor,
        yAccessor,
        sizeAccessor,
        labelAccessor,
        dataFormat,
        palette,
        seriesIdPrefix: restProps.seriesId || 'scatter'
      })
    }

    // Default: treat data as simple array
    return { data: Array.isArray(data) ? data : [] }
  }, [
    data, variant, xAccessor, yAccessor, sizeAccessor, labelAccessor, seriesAccessor,
    seriesNameAccessor, seriesDataAccessor, dataFormat, datasetSource, datasetDimensions,
    encode, palette, restProps.seriesId
  ])

  // Compute symbol size function from scaling mode
  const computedSymbolSize = useMemo(() => {
    if (typeof symbolSize === 'number') {
      return symbolSize
    }

    if (!processedData?.sizeExtent) {
      return sizeMin
    }

    if (symbolSize === 'linear') {
      const [dataMin, dataMax] = processedData.sizeExtent
      const domain = dataMax - dataMin
      if (domain === 0) return sizeMin
      return val => {
        const raw = val[2]
        const t = (raw - dataMin) / domain
        return sizeMin + t * (sizeMax - sizeMin)
      }
    }

    if (symbolSize === 'log') {
      const [dataMin, dataMax] = processedData.sizeExtent
      const logMin = Math.log10(dataMin)
      const logMax = Math.log10(dataMax)
      const logDomain = logMax - logMin
      if (logDomain === 0) return sizeMin
      return val => {
        const raw = val[2]
        const logVal = Math.log10(raw)
        const t = (logVal - logMin) / logDomain
        return sizeMin + t * (sizeMax - sizeMin)
      }
    }

    if (symbolSize === 'exp') {
      const [dataMin, dataMax] = processedData.sizeExtent
      const domain = dataMax - dataMin
      if (domain === 0) return sizeMin
      return val => {
        const raw = val[2]
        const t = (raw - dataMin) / domain
        const tExp = (Math.exp(t) - 1) / (Math.E - 1)
        return sizeMin + tExp * (sizeMax - sizeMin)
      }
    }

    if (symbolSize === 'outlier') {
      if (!processedData?.sizeStats) return sizeMin
      const { mean, std } = processedData.sizeStats
      const maxZ = 3
      return val => {
        const raw = val[2]
        const z = std ? (raw - mean) / std : 0
        const t = Math.min(Math.abs(z) / maxZ, 1)
        return sizeMin + t * (sizeMax - sizeMin)
      }
    }

    return symbolSize
  }, [symbolSize, sizeMin, sizeMax, processedData?.sizeExtent, processedData?.sizeStats])

  // Process props to intercept format strings
  const processedProps = useMemo(() => {
    return interceptFormatters({
      symbolSize: computedSymbolSize,
      ...restProps
    }, valueFormatter, xFormatter)
  }, [restProps, valueFormatter, xFormatter, computedSymbolSize])

  // Set loading state
  useEffect(() => {
    // All data comes from a single pipeline response
    setSeriesLoading(restProps.seriesId, isLoading)
  }, [isLoading, restProps.seriesId, setSeriesLoading])

  if (isLoading) return null

  // Handle dataset pattern
  if (processedData?.dataset) {
    return (
      <ComponentScatter
        dataset={processedData.dataset}
        encode={processedData.encode}
        {...processedProps}
      />
    )
  }

  // Render based on variant
  if (variant === 'simple' || !processedData?.series) {
    return (
      <ComponentScatter
        data={processedData?.data || []}
        {...processedProps}
      />
    )
  }

  // Multi-series rendering
  return (
    <>
      {processedData.series.map(series => {
        // Only intercept formatters for series-specific options
        const seriesSpecificProps = series.options
          ? interceptFormatters(series.options, valueFormatter, xFormatter) : {}

        return (
          <ComponentScatter
            key={series.id}
            seriesId={series.id}
            name={series.name}
            data={series.data}
            {...processedProps}
            {...seriesSpecificProps}
          />
        )
      })}
    </>
  )
}

// Process simple scatter data
function processSimpleData (data, config) {
  if (!Array.isArray(data)) return { data: [] }

  let minSize = Infinity
  let maxSize = -Infinity
  let sum = 0
  let sumSq = 0
  let count = 0

  // First pass: build raw series data and calculate statistics
  const rawSeries = data.map(item => {
    // Handle different data formats
    let x, y, size, label

    if (config.dataFormat === 'array' || Array.isArray(item)) {
      x = item[0]
      y = item[1]
      size = item[2]
    } else if (config.dataFormat === 'xy' || (item.x !== undefined && item.y !== undefined)) {
      x = config.xAccessor ? get(item, config.xAccessor) : item.x
      y = config.yAccessor ? get(item, config.yAccessor) : item.y
      if (config.sizeAccessor) {
        size = Number(get(item, config.sizeAccessor))
      }
    } else {
      // Use accessors
      x = config.xAccessor ? get(item, config.xAccessor) : item[0]
      y = config.yAccessor ? get(item, config.yAccessor) : item[1]
      if (config.sizeAccessor) {
        size = Number(get(item, config.sizeAccessor))
      }
    }

    // Extract label if accessor provided
    if (config.labelAccessor) {
      label = String(get(item, config.labelAccessor))
    }

    // Calculate size statistics
    if (size !== undefined) {
      if (size < minSize) minSize = size
      if (size > maxSize) maxSize = size
      sum += size
      sumSq += size * size
      count += 1
    }

    const value = size !== undefined ? [x, y, size] : [x, y]
    return { value, label }
  })

  // Calculate mean and standard deviation for size
  const mean = config.sizeAccessor && count ? sum / count : 0
  const variance = config.sizeAccessor && count ? sumSq / count - mean * mean : 0
  const std = variance > 0 ? Math.sqrt(variance) : 0

  // Second pass: format the series data with labels and/or palette
  let series
  if (Array.isArray(config.palette) && config.palette.length) {
    series = rawSeries.map((d, i) => {
      const point = { value: d.value }
      if (config.labelAccessor && d.label != null) point.name = d.label
      point.itemStyle = { color: config.palette[i % config.palette.length] }
      return point
    })
  } else if (config.labelAccessor) {
    series = rawSeries.map(d => {
      const point = { value: d.value }
      if (d.label != null) point.name = d.label
      return point
    })
  } else {
    series = rawSeries.map(d => d.value)
  }

  return {
    data: series,
    sizeExtent: config.sizeAccessor ? [minSize, maxSize] : null,
    sizeStats: config.sizeAccessor ? { mean, std } : null
  }
}

// Process multi-series data
function processMultiSeriesData (seriesData, config) {
  if (!Array.isArray(seriesData)) return { series: [] }

  const {
    seriesNameAccessor,
    seriesDataAccessor,
    xAccessor,
    yAccessor,
    sizeAccessor,
    labelAccessor,
    palette,
    seriesIdPrefix
  } = config

  let minSize = Infinity
  let maxSize = -Infinity
  let sum = 0
  let sumSq = 0
  let count = 0

  const series = seriesData.map((item, index) => {
    const name = seriesNameAccessor
      ? get(item, seriesNameAccessor)
      : (item.name || `Series ${index + 1}`)

    const rawData = seriesDataAccessor
      ? get(item, seriesDataAccessor)
      : (item.data || item)

    // Process series data items
    let processedData = rawData
    if (Array.isArray(rawData) && (xAccessor || yAccessor)) {
      const rawPoints = rawData.map(dataItem => {
        if (Array.isArray(dataItem)) {
          const size = dataItem[2]
          if (size !== undefined) {
            const numSize = Number(size)
            if (numSize < minSize) minSize = numSize
            if (numSize > maxSize) maxSize = numSize
            sum += numSize
            sumSq += numSize * numSize
            count += 1
          }
          return { value: dataItem }
        }

        const x = xAccessor ? get(dataItem, xAccessor) : dataItem.x
        const y = yAccessor ? get(dataItem, yAccessor) : dataItem.y
        const label = labelAccessor ? String(get(dataItem, labelAccessor)) : undefined

        // Bubble chart: [x, y, size]
        if (sizeAccessor) {
          const size = Number(get(dataItem, sizeAccessor))
          if (size < minSize) minSize = size
          if (size > maxSize) maxSize = size
          sum += size
          sumSq += size * size
          count += 1
          return { value: [x, y, size], label }
        }

        return { value: [x, y], label }
      })

      // Format with labels and/or palette
      if (Array.isArray(palette) && palette.length) {
        processedData = rawPoints.map((d, i) => {
          const point = { value: d.value }
          if (labelAccessor && d.label != null) point.name = d.label
          point.itemStyle = { color: palette[i % palette.length] }
          return point
        })
      } else if (labelAccessor) {
        processedData = rawPoints.map(d => {
          const point = { value: d.value }
          if (d.label != null) point.name = d.label
          return point
        })
      } else {
        processedData = rawPoints.map(d => d.value)
      }
    }

    return {
      id: `${seriesIdPrefix}_${name}_${index}`,
      name,
      data: processedData || [],
      options: item.options || {}
    }
  })

  // Calculate mean and standard deviation for size
  const mean = sizeAccessor && count ? sum / count : 0
  const variance = sizeAccessor && count ? sumSq / count - mean * mean : 0
  const std = variance > 0 ? Math.sqrt(variance) : 0

  return {
    series,
    sizeExtent: sizeAccessor ? [minSize, maxSize] : null,
    sizeStats: sizeAccessor ? { mean, std } : null
  }
}

DDPScatterPlot.propTypes = {
  // Pipeline configuration
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,

  // Variant
  variant: PropTypes.oneOf(['simple', 'multi-series']),

  // Accessor-based data mapping
  xAccessor: PropTypes.string,
  yAccessor: PropTypes.string,
  sizeAccessor: PropTypes.string,
  labelAccessor: PropTypes.string,
  seriesAccessor: PropTypes.string,
  seriesNameAccessor: PropTypes.string,
  seriesDataAccessor: PropTypes.string,

  // Symbol size configuration
  symbolSize: PropTypes.oneOfType([
    PropTypes.oneOf(['linear', 'log', 'exp', 'outlier']),
    PropTypes.number
  ]),
  sizeMin: PropTypes.number,
  sizeMax: PropTypes.number,

  // Color palette
  palette: PropTypes.arrayOf(PropTypes.string),

  // Data format hints
  dataFormat: PropTypes.oneOf(['array', 'xy', 'object']),

  // Dataset pattern
  datasetSource: PropTypes.string,
  datasetDimensions: PropTypes.arrayOf(PropTypes.string),
  encode: PropTypes.object,

  // Formatting
  valueFormat: PropTypes.string,
  xFormat: PropTypes.string,
  yFormat: PropTypes.string
}

DDPScatterPlot.defaultProps = {
  variant: 'simple',
  valueFormat: 'number',
  dataFormat: 'object',
  seriesAccessor: 'data',
  seriesNameAccessor: 'name',
  seriesDataAccessor: 'data',
  symbolSize: 8,
  sizeMin: 5,
  sizeMax: 50
}

export default DDPScatterPlot
